import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Stack,
  Table,
  TableCaption,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";

const ResultsPage: React.FC<{
  navigate: () => void;
  subject: string;
  totalQuestions: number;
  quizResults: {
    questionsAnswered: number;
    totalQuizGrade: number;
    passRate: number;
    passedQuiz: boolean;
  };
  canRetakeTest?: boolean;
  navigateToSubject: () => void;
  canEditSubject?: boolean;
}> = observer(
  ({
    navigate,
    subject,
    totalQuestions,
    quizResults,
    canRetakeTest = false,
    navigateToSubject = () => {},
    canEditSubject = true,
  }) => {
    return (
      <Stack justifyContent="stretch" alignItems="stretch" spacing={[6, 8]}>
        {quizResults.passedQuiz ? (
          <VStack textAlign="center" spacing={4}>
            <CheckCircleIcon color="green.500" fontSize="3xl" />
            <Text lineHeight="shorter" fontWeight="black" fontSize="3xl">
              Congrats, you passed!
            </Text>
            <Text>
              Good job! Now you can go on to create your {subject} profile, set
              your price and start getting students! Here's your result summary:
            </Text>
          </VStack>
        ) : (
          <VStack textAlign="center" spacing={4}>
            <CloseIcon fontSize="3xl" color="red.500" />
            <Text lineHeight="shorter" fontWeight="black" fontSize="3xl">
              Sadly, you didn't pass this test
            </Text>
            <Text>
              This means you won't be able to teach {subject} at this time. If
              this is your first attempt, you'll have two more attempts to
              retake the test within 48 hours. Here's your result summary:
            </Text>
          </VStack>
        )}

        <Box p={6} borderWidth="2xs" borderColor="neutral.500" rounded="md">
          <Table variant="simple">
            <TableCaption>{subject} test summary</TableCaption>
            <Thead>
              <Tr>
                <Th>Description</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              <Tr>
                <Td>Questions answered</Td>
                <Td>{`${quizResults.questionsAnswered} / ${totalQuestions}`}</Td>
              </Tr>
              <Tr>
                <Td>Your score</Td>
                <Td>{`${Math.round(quizResults.totalQuizGrade)}%`}</Td>
              </Tr>
              <Tr>
                <Td>Pass mark</Td>
                <Td>{`${Math.round(quizResults.passRate)}%`}</Td>
              </Tr>
              <Tr>
                <Td>Status</Td>
                <Td
                  fontWeight="medium"
                  color={quizResults.passedQuiz ? "green.500" : "red.500"}
                >
                  {quizResults.passedQuiz ? "Passed" : "Failed"}
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
        {quizResults.passedQuiz ? (
          canEditSubject ? (
            <Button
              size="lg"
              colorScheme="blue"
              rightIcon={<ChevronRightIcon />}
              onClick={navigate}
            >
              Create your subject
            </Button>
          ) : (
            <Button
              m="5px"
              size="lg"
              colorScheme="blue"
              leftIcon={<ChevronLeftIcon />}
              onClick={navigateToSubject}
            >
              Back to Subjects
            </Button>
          )
        ) : (
          <Flex
            direction={["column", "row"]}
            justify={canRetakeTest ? "space-between" : "center"}
          >
            {canRetakeTest && (
              <Button
                m="5px"
                size="lg"
                colorScheme="blue"
                leftIcon={<ChevronLeftIcon />}
                onClick={navigateToSubject}
              >
                Back to Subjects
              </Button>
            )}
            <Button
              m="5px"
              size="lg"
              colorScheme="blue"
              leftIcon={<ChevronLeftIcon />}
              onClick={navigate}
            >
              {canRetakeTest ? "Retake Test" : "Add a new subject"}
            </Button>
          </Flex>
        )}
      </Stack>
    );
  },
);

export default ResultsPage;
